import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DxDropDownBoxModule } from 'devextreme-angular';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxPopupModule } from 'devextreme-angular/ui/popup';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTreeViewModule } from 'devextreme-angular/ui/tree-view';
import {
  FieldOption,
  LookupCriterion,
  ManualLookupConfiguration,
  MappingGridRow,
  MappingPicklistOption,
  TreeNode
} from '../../models/link-generic-mapper.models';

interface ReturnFieldOptionGroup {
	key: string;
	items: FieldOption[];
}

@Component({
  selector: 'app-lookup-editor-popup',
  standalone: true,
  imports: [
    CommonModule,
    DxPopupModule,
    DxButtonModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxDropDownBoxModule,
    DxTreeViewModule
  ],
  templateUrl: './lookup-editor-popup.component.html',
  styleUrl: './lookup-editor-popup.component.scss'
})
export class LookupEditorPopupComponent implements OnChanges {
	readonly applyLookupButtonElementAttr = { 'data-cy': 'mapper-apply-lookup-button' };
	readonly testLookupButtonElementAttr = { 'data-cy': 'mapper-test-lookup-button' };
	readonly returnFieldElementAttr = { 'data-cy': 'mapper-lookup-return-field' };
	readonly returnFieldDisplayExpr = (field: FieldOption | null): string => {
		if (!field) {
			return '';
		}

		return this.isDefaultReturnField(field)
			? `${field.label} (Recommended)`
			: field.label;
	};
	readonly sourceFieldDisplayExpr = (field: FieldOption | null): string => {
		if (!field) {
			return '';
		}

		return field.label === field.path
			? field.path
			: `${field.label} (${field.path})`;
	};

  @Input() visible = false;
  @Input() row: MappingGridRow | null = null;
  @Input() sourceFieldOptions: FieldOption[] = [];
  @Input() sourceTreeItems: TreeNode[] = [];
  @Input() referenceFieldOptions: FieldOption[] = [];
	@Input() sourceSystemName = '';
	@Input() sampleSourcePayload: unknown = null;
	@Input() canTestLookup = false;
	@Input() testLoading = false;
	@Input() testRan = false;
	@Input() testValid = false;
	@Input() testHasResult = false;
	@Input() testResult: unknown = null;
	@Input() testMessage = '';

	criterionModeOptions = [
		{ value: 'SourceField', label: 'Source field' },
    { value: 'Constant', label: 'Fixed value' },
    { value: 'Expression', label: 'Expression' }
  ];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<void>();
	@Output() apply = new EventEmitter<ManualLookupConfiguration>();

  @Output() criterionExpressionEditRequested = new EventEmitter<number>();
	@Output() deferredSourceNodeExpanded = new EventEmitter<TreeNode>();
	@Output() testLookup = new EventEmitter<ManualLookupConfiguration>();
	@Output() draftChanged = new EventEmitter<void>();

  private sourceFieldSearchByCriterion = new WeakMap<LookupCriterion, string>();
  private sourceDropdownOpenedByCriterion = new WeakMap<LookupCriterion, boolean>();
  private sourceTreeItemsByCriterion = new WeakMap<LookupCriterion, TreeNode[]>();
	private cachedReturnFieldOptionGroups: ReturnFieldOptionGroup[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row'] || changes['sourceTreeItems'] || changes['sourceFieldOptions']) {
      this.sourceTreeItemsByCriterion = new WeakMap<LookupCriterion, TreeNode[]>();
    }

		if (changes['row'] || changes['referenceFieldOptions']) {
			this.rebuildReturnFieldOptionGroups();
		}

		if (changes['sourceSystemName']) {
			this.criterionModeOptions = [
				{ value: 'SourceField', label: `${this.sourceSystemLabel} field` },
				{ value: 'Constant', label: 'Fixed value' },
				{ value: 'Expression', label: 'Expression' }
			];
		}
  }

  get lookup(): ManualLookupConfiguration | null {
    if (!this.row) {
      return null;
    }

    if (!this.row.lookup) {
      this.row.lookup = {
        referenceEntityName:
          this.row.referenceEntityName ||
          this.row.reference?.targetEntity ||
          '',
        returnFieldPath:
          this.row.reference?.defaultReturnFieldPath || 'ID',
        matchMode: 'All',
        criteria: []
      };
    }

    return this.row.lookup;
  }

	applyLookup(): void {
		if (this.lookup && this.canApplyLookup) {
			this.apply.emit(this.lookup);
		}
	}

	testCurrentLookup(): void {
		if (this.lookup && this.canApplyLookup && this.canTestLookup && !this.testLoading) {
			this.testLookup.emit(this.lookup);
		}
	}

	get canApplyLookup(): boolean {
		return !!this.lookup?.returnFieldPath
			&& this.lookup.criteria.length > 0
			&& this.lookup.criteria.every(criterion => this.isCriterionComplete(criterion));
	}

	get applyDisabledReason(): string {
		if (!this.lookup?.returnFieldPath) {
			return 'Choose a return field or key to continue.';
		}

		if (!this.lookup.criteria.length) {
			return 'Add a matching condition to continue.';
		}

		const incompleteCriterion = this.lookup.criteria.find(criterion => !this.isCriterionComplete(criterion));
		if (!incompleteCriterion) {
			return '';
		}

		if (!incompleteCriterion.targetFieldPath) {
			return `Choose a ${this.referenceEntityLabel} match field to continue.`;
		}

		if (incompleteCriterion.mode === 'SourceField') {
			return `Choose a ${this.sourceSystemLabel} field to compare.`;
		}

		if (incompleteCriterion.mode === 'Constant') {
			return 'Enter a fixed value to compare.';
		}

		return 'Add an expression to compare.';
	}

	setMatchMode(mode: 'All' | 'Any'): void {
		if (this.lookup) {
			this.lookup.matchMode = mode;
			this.notifyDraftChanged();
		}
	}

	notifyDraftChanged(): void {
		this.draftChanged.emit();
	}

	onReturnFieldChanged(value: string | null): void {
		if (!this.lookup) {
			return;
		}

		this.lookup.returnFieldPath = value || '';
		this.notifyDraftChanged();
	}

	get defaultReturnFieldPath(): string {
		return this.row?.reference?.defaultReturnFieldPath || 'ID';
	}

	get sourceSystemLabel(): string {
		return this.sourceSystemName?.trim() || 'Source';
	}

	get returnFieldOptionGroups(): ReturnFieldOptionGroup[] {
		return this.cachedReturnFieldOptionGroups;
	}

  get referenceEntityLabel(): string {
    return (
      this.lookup?.referenceEntityName ||
      this.row?.referenceEntityName ||
      this.row?.reference?.targetEntity ||
      'related entity'
    );
  }

  get returnFieldLabel(): string {
    return this.getReferenceFieldLabel(this.lookup?.returnFieldPath ?? null) || 'a selected field';
  }

	get lookupExecutionSummary(): string {
		if (!this.lookup?.criteria.length) {
			return `Add a condition to find one record in ${this.referenceEntityLabel}.`;
		}

		const joiner = this.lookup.matchMode === 'Any' ? ' or ' : ' and ';
		const conditions = this.lookup.criteria.map(criterion => this.getCriterionExecutionSummary(criterion)).join(joiner);
		return `Find ${this.referenceEntityLabel} where ${conditions}, then return ${this.returnFieldLabel}.`;
	}

	get optionalLookupSkipMessage(): string {
		if (this.row?.required || !this.lookup) {
			return '';
		}

		const emptySourceCriterion = this.lookup.criteria.find(criterion =>
			criterion.mode === 'SourceField'
			&& !!criterion.sourceFieldPath
			&& this.isCriterionSampleValueEmpty(criterion));
		const sourceLabel = this.getSourceFieldLabel(emptySourceCriterion?.sourceFieldPath ?? null);
		return sourceLabel
			? `If ${sourceLabel} is empty, this optional lookup is skipped.`
			: '';
	}

	getCriterionJoiner(index: number): string {
		if (index === 0) {
			return 'Where';
		}

		return this.lookup?.matchMode === 'Any' ? 'Or' : 'And';
	}

	get testResultDisplay(): string {
		return this.formatSampleValue(this.testResult);
	}

  addCriterion(): void {
    if (!this.lookup) {
      return;
    }

    const criterion: LookupCriterion = {
      targetFieldPath: '',
      mode: 'SourceField',
      sourceFieldPath: null,
      value: null
    };

    this.lookup.criteria.push(criterion);
    this.rebuildCriterionSourceTree(criterion);
		this.notifyDraftChanged();
  }

  removeCriterion(index: number): void {
    if (!this.lookup) {
      return;
    }

    if (index >= 0 && index < this.lookup.criteria.length) {
      this.lookup.criteria.splice(index, 1);
			this.notifyDraftChanged();
    }
  }

  onCriterionModeChanged(
    criterion: LookupCriterion,
    value: 'SourceField' | 'Constant' | 'Expression' | null
  ): void {
    criterion.mode = value || 'SourceField';
		this.notifyDraftChanged();

    if (criterion.mode === 'SourceField') {
      criterion.sourceFieldPath = criterion.sourceFieldPath ?? null;
      criterion.value = null;
      return;
    }

    criterion.sourceFieldPath = null;
    criterion.value ??= '';
  }

  onCriterionTargetFieldChanged(
    criterion: LookupCriterion,
    value: string | null
  ): void {
    criterion.targetFieldPath = value || '';
		this.notifyDraftChanged();

    const targetField = this.getCriterionTargetFieldOption(criterion);

    if (criterion.mode === 'Constant' && targetField?.isPicklist) {
      const currentValue = criterion.value ?? '';
      const options = this.getCriterionConstantPicklistOptions(criterion);

      const stillExists = options.some(x => x.value === currentValue);
      if (!stillExists) {
        criterion.value = null;
      }
    }
  }

	onSourceTreeItemExpanded(event: any): void {
		const item = event?.itemData as TreeNode | undefined;
		if (item?.childrenDeferred && item.deferredLoadState !== 'loading') {
			this.deferredSourceNodeExpanded.emit(item);
		}
	}

  editCriterionExpression(index: number): void {
    this.criterionExpressionEditRequested.emit(index);
  }

  insertReferenceField(field: FieldOption): void {
    if (!this.lookup?.criteria?.length) {
      return;
    }

    const last = this.lookup.criteria[this.lookup.criteria.length - 1];
    last.targetFieldPath = field.path;
  }

  insertSourceField(field: FieldOption): void {
    if (!this.lookup?.criteria?.length) {
      return;
    }

    const last = this.lookup.criteria[this.lookup.criteria.length - 1];

    if (last.mode === 'SourceField') {
      last.sourceFieldPath = field.path;
    } else {
      last.value = `[${field.path}]`;
    }
  }

	configureLookupFromEvent(event: Event): void {
		if (!this.lookup) {
			return;
		}

		const detail = (event as CustomEvent<{
			returnFieldPath?: string | null;
			matchMode?: 'All' | 'Any' | null;
			criteria?: Array<{
				targetFieldPath?: string | null;
				mode?: 'SourceField' | 'Constant' | 'Expression' | null;
				sourceFieldPath?: string | null;
				value?: string | null;
			}>;
		}>).detail;

		if (!detail) {
			return;
		}

		event.stopPropagation();

		const returnFieldPath = detail.returnFieldPath?.trim();
		if (returnFieldPath && this.referenceFieldOptions.some(option => option.path === returnFieldPath)) {
			this.lookup.returnFieldPath = returnFieldPath;
		}

		this.lookup.matchMode = detail.matchMode === 'Any' ? 'Any' : 'All';

		const criteria = (detail.criteria ?? [])
			.map(criterion => this.normalizeCriterionFromEvent(criterion))
			.filter((criterion): criterion is LookupCriterion => !!criterion);

		if (criteria.length) {
			this.lookup.criteria = criteria;
			this.notifyDraftChanged();
		}
	}

  getCriterionTargetFieldOption(criterion: LookupCriterion): FieldOption | null {
    if (!criterion?.targetFieldPath) {
      return null;
    }

    return this.referenceFieldOptions.find(x => x.path === criterion.targetFieldPath) ?? null;
  }

  isCriterionTargetPicklist(criterion: LookupCriterion): boolean {
    return !!this.getCriterionTargetFieldOption(criterion)?.isPicklist;
  }

  getCriterionConstantPicklistOptions(criterion: LookupCriterion): MappingPicklistOption[] {
    const field = this.getCriterionTargetFieldOption(criterion);
    return (field?.picklistOptions ?? []).filter(x => x.isActive !== false);
  }

  getCriterionTargetFieldHint(criterion: LookupCriterion): string | null {
    const field = this.getCriterionTargetFieldOption(criterion);
    if (!field) {
      return null;
    }

    const parts: string[] = [];

    if (field.isPicklist) {
      parts.push('Picklist field');
    }

    if (field.description) {
      parts.push(field.description);
    }

    return parts.length ? parts.join(' · ') : null;
  }

	getCriterionSampleValue(criterion: LookupCriterion): unknown {
		if (criterion.mode !== 'SourceField' || !criterion.sourceFieldPath) {
			return undefined;
		}

		return criterion.sourceFieldPath
			.split('.')
			.reduce((value: any, part) => value?.[part], this.sampleSourcePayload as any);
	}

	getCriterionSampleValueDisplay(criterion: LookupCriterion): string {
		return this.formatSampleValue(this.getCriterionSampleValue(criterion));
	}

	isCriterionSampleValueEmpty(criterion: LookupCriterion): boolean {
		const value = this.getCriterionSampleValue(criterion);
		return value === null || value === undefined || value === '';
	}

  getCriterionSourceFieldSearchValue(criterion: LookupCriterion): string {
    return this.sourceFieldSearchByCriterion.get(criterion) ?? '';
  }

  setCriterionSourceFieldSearchValue(
    criterion: LookupCriterion,
    value: string | null | undefined
  ): void {
    this.sourceFieldSearchByCriterion.set(criterion, value ?? '');
    this.rebuildCriterionSourceTree(criterion);
  }

  isCriterionSourceDropdownOpened(criterion: LookupCriterion): boolean {
    return this.sourceDropdownOpenedByCriterion.get(criterion) ?? false;
  }

  setCriterionSourceDropdownOpened(
    criterion: LookupCriterion,
    value: boolean
  ): void {
    this.sourceDropdownOpenedByCriterion.set(criterion, value);
  }

  clearCriterionSourceField(criterion: LookupCriterion): void {
    criterion.sourceFieldPath = null;
		this.notifyDraftChanged();
    this.rebuildCriterionSourceTree(criterion);
    this.setCriterionSourceDropdownOpened(criterion, false);
  }

  getCachedCriterionSourceTreeItems(criterion: LookupCriterion): TreeNode[] {
    const cached = this.sourceTreeItemsByCriterion.get(criterion);
    if (cached) {
      return cached;
    }

    this.rebuildCriterionSourceTree(criterion);
    return this.sourceTreeItemsByCriterion.get(criterion) ?? [];
  }

  private rebuildCriterionSourceTree(criterion: LookupCriterion): void {
    const allowedPaths = new Set(this.sourceFieldOptions.map(x => x.path));
    const query = this.getCriterionSourceFieldSearchValue(criterion).trim().toLowerCase();
    const selectedPath = criterion.sourceFieldPath ?? null;

    const mapNode = (node: TreeNode, parentMatchesSearch = false): TreeNode | null => {
      const originalChildren = node.items ?? [];
			const haystack = `${node.displayName ?? ''} ${node.path ?? ''} ${node.dataType ?? ''}`.toLowerCase();
			const matchesSearch = !query || parentMatchesSearch || haystack.includes(query);

      const mappedChildren = originalChildren
        .map(child => mapNode(child, matchesSearch))
        .filter((x): x is TreeNode => !!x);

      const hadChildrenOriginally = originalChildren.length > 0;
      const hasVisibleChildren = mappedChildren.length > 0;
		const isLeaf = !hadChildrenOriginally && !node.childrenDeferred;
      const isAllowedLeaf = !!node.isMappable && allowedPaths.has(node.path);
      const isSelectedLeaf = !!selectedPath && node.path === selectedPath;

      const includeNode =
        matchesSearch ||
        hasVisibleChildren ||
        (!query && isAllowedLeaf) ||
        isSelectedLeaf;

      if (!includeNode) {
        return null;
      }

      return {
        ...node,
        items: mappedChildren,
			disabled: isLeaf ? !isAllowedLeaf : false,
			expanded: node.expanded === true || (!!query && mappedChildren.length > 0)
      };
    };

    const result = (this.sourceTreeItems ?? [])
      .map(node => mapNode(node))
      .filter((x): x is TreeNode => !!x);

    this.sourceTreeItemsByCriterion.set(criterion, result);
  }

	private normalizeCriterionFromEvent(criterion: {
		targetFieldPath?: string | null;
		mode?: 'SourceField' | 'Constant' | 'Expression' | null;
		sourceFieldPath?: string | null;
		value?: string | null;
	}): LookupCriterion | null {
		const targetFieldPath = criterion.targetFieldPath?.trim();
		if (!targetFieldPath || !this.referenceFieldOptions.some(option => option.path === targetFieldPath)) {
			return null;
		}

		const mode = criterion.mode || 'SourceField';
		if (mode === 'SourceField') {
			const sourceFieldPath = criterion.sourceFieldPath?.trim();
			if (!sourceFieldPath || !this.sourceFieldOptions.some(option => option.path === sourceFieldPath)) {
				return null;
			}

			return {
				targetFieldPath,
				mode,
				sourceFieldPath,
				value: null
			};
		}

		return {
			targetFieldPath,
			mode,
			sourceFieldPath: null,
			value: criterion.value ?? null
		};
	}

  onCriterionSourceTreeItemClick(e: any, criterion: LookupCriterion): void {
    const item = e?.itemData as TreeNode | undefined;
		if (item?.childrenDeferred) {
			this.deferredSourceNodeExpanded.emit(item);
			return;
		}

    if (!item || item.items?.length || item.disabled || !item.isMappable) {
      return;
    }

    criterion.sourceFieldPath = item.path;
		this.notifyDraftChanged();
    this.rebuildCriterionSourceTree(criterion);
    this.setCriterionSourceDropdownOpened(criterion, false);
  }

	onCriterionSourceFieldChanged(criterion: LookupCriterion, value: string | null): void {
		criterion.sourceFieldPath = value ?? null;
		this.notifyDraftChanged();
	}

	onCriterionValueChanged(criterion: LookupCriterion, value: string | null): void {
		criterion.value = value;
		this.notifyDraftChanged();
	}

	private isCriterionComplete(criterion: LookupCriterion): boolean {
		if (!criterion.targetFieldPath) {
			return false;
		}

		if (criterion.mode === 'SourceField') {
			return !!criterion.sourceFieldPath;
		}

		return !!criterion.value?.trim();
	}

	private getCriterionExecutionSummary(criterion: LookupCriterion): string {
		const targetLabel = this.getReferenceFieldLabel(criterion.targetFieldPath) || '[choose match field]';

		if (criterion.mode === 'SourceField') {
			return `${targetLabel} equals ${this.getSourceFieldLabel(criterion.sourceFieldPath) || `[choose ${this.sourceSystemLabel} field]`}`;
		}

		if (criterion.mode === 'Constant') {
			return `${targetLabel} equals ${criterion.value?.trim() ? `"${criterion.value.trim()}"` : '[enter fixed value]'}`;
		}

		return `${targetLabel} equals ${criterion.value?.trim() ? 'the expression result' : '[add expression]'}`;
	}

	private rebuildReturnFieldOptionGroups(): void {
		const options = [...this.referenceFieldOptions].sort((a, b) => {
			const defaultDifference = Number(this.isDefaultReturnField(b)) - Number(this.isDefaultReturnField(a));
			return defaultDifference || a.label.localeCompare(b.label);
		});
		const keys = options.filter(option => option.isKey || option.isUniqueKey || this.isDefaultReturnField(option));
		const otherFields = options.filter(option => !keys.includes(option));
		this.cachedReturnFieldOptionGroups = [
			...(keys.length ? [{ key: 'Keys', items: keys }] : []),
			...(otherFields.length ? [{ key: 'Other readable fields', items: otherFields }] : [])
		];
	}

	private isDefaultReturnField(field: FieldOption): boolean {
		return field.path.toLowerCase() === this.defaultReturnFieldPath.toLowerCase();
	}

	private formatSampleValue(value: unknown): string {
		if (value === null || value === undefined || value === '') {
			return 'Empty';
		}

		if (typeof value === 'string') {
			return value;
		}

		try {
			return JSON.stringify(value);
		} catch {
			return String(value);
		}
	}

  private getReferenceFieldLabel(path: string | null | undefined): string | null {
    if (!path) {
      return null;
    }

    return this.referenceFieldOptions.find(x => x.path === path)?.label ?? path;
  }

  getSourceFieldLabel(path: string | null | undefined): string | null {
    if (!path) {
      return null;
    }

    return this.sourceFieldOptions.find(x => x.path === path)?.label ?? path;
  }
}
